import React, { useEffect, useState, useRef, useLayoutEffect, useCallback } from 'react';
import {
    Box, CssBaseline, AppBar, Toolbar, IconButton,
    Typography, Avatar, Paper,
    useTheme, CircularProgress,
    Drawer,
    Slide,
    Menu, MenuItem,
    ListItemIcon,
    Tooltip,
    useMediaQuery,
    Stack,
    Badge,
    Button
} from '@mui/material';
import {
    Groups, ArrowBack, MoreVert,
    Close,
    DoneAll,
    Check,
    EmojiEmotions,
    EmojiEmotionsOutlined, InfoOutline,
    DeleteForeverOutlined,
    RemoveCircleOutline,
    VerifiedUser,
    ChatBubbleRounded
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getImageUrlWithToken } from '../../utils/ImageUtils';
import { useMessageContext } from '../../context/MessageProvider';
import { getMessages, createChat, getChats, deleteChat, addReaction, messageSeen, clearChat, getNewMessageCount, markRead } from '../../services/Chat';
import RecentChatList from './RecentChatList';
import ConnectionList from './ConnectionList';
import Branding from './Branding';
import EmptyState from './EmptyState';
import MessageInput from './MessageInput';
import ReadMoreText from '../../utils/ReadMoretext';
import ImagePreviewDialog from './ImagePreviewDialog';
import MentorBadge from '../../components/MentorBadge';
import ChatDetail from './ChatDetail';
import DeleteMessage from './DeleteMessage';
import { formatDisplayName } from '../../utils/formatDisplayName';


const Messages = ({ user, showSnackbar, open, onClose, initialContact, bottombar }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { Id } = useParams();

    // Get initialContact from location state if not provided via props
    const contactToOpen = initialContact || location.state?.initialContact;
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [selectedChat, setSelectedChat] = useState(null);
    const [sidebarView, setSidebarView] = useState('recent');
    const { data } = useMessageContext();
    const bottomRef = useRef(null);

    console.log("showing messages component", { user, showSnackbar, open, onClose, initialContact, contactFromState: location.state?.initialContact });

    // NEW: Refs for Manual Scrolling
    const scrollRef = useRef(null);
    const prevHeightRef = useRef(null);
    const lastProcessedMsgIdRef = useRef(null);

    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewImageUrl, setPreviewImageUrl] = useState("");
    const [recentChats, setRecentChats] = useState([]);
    const [chatPage, setChatPage] = useState(1);
    const [chatHasMore, setChatHasMore] = useState();
    const [isDetailOpen, setIsDetailOpen] = useState(false);

    // MESSAGE STATES
    const [messages, setMessages] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isFetching, setIsFetching] = useState(false); // NEW: Lock to prevent double fetching
    const loadingRef = useRef(false);

    const [isBlocked, setIsBlocked] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [showMsg, setShowMsg] = useState(false);
    const [receipientId, setReceipientId] = useState(null);


    const handleChatSelect = (contact) => {
        setSelectedChat(contact);
        setIsDetailOpen(false);
        // Navigate to the chat URL
        navigate(`/chats/${contact.chat_id}`, { replace: true });
        // Reset unread messages count
        setRecentChats((prevChats) =>
            prevChats.map((chat) =>
                chat.chat_id === contact.chat_id
                    ? { ...chat, unread_messages: 0 }
                    : chat
            )
        );
    };

    useEffect(() => {
        if (open && contactToOpen) {
            console.log('Initial contact received:', contactToOpen);
            handleStartNewChat(contactToOpen);
        }
    }, [open, contactToOpen]);

    // Auto-select chat based on URL parameter
    useEffect(() => {
        if (Id && recentChats.length > 0 && !selectedChat) {
            const chatFromUrl = recentChats.find(chat => chat.chat_id === parseInt(Id));
            if (chatFromUrl) {
                setSelectedChat(chatFromUrl);
                setIsDetailOpen(false);
                // Reset unread messages count
                setRecentChats((prevChats) =>
                    prevChats.map((chat) =>
                        chat.chat_id === parseInt(Id)
                            ? { ...chat, unread_messages: 0 }
                            : chat
                    )
                );
            }
        }
    }, [Id, recentChats, selectedChat]);


    const handleStartNewChat = async (newChatObj) => {
        console.log("Starting chat with:", newChatObj);

        let activeChat = newChatObj;

        // If there is no chat_id (fresh chat), we might need to ask the backend for one
        if (!newChatObj.chat_id) {
            const ids = Array.isArray(newChatObj.user_id)
                ? newChatObj.user_id
                : [newChatObj.user_id];
            try {
                const response = await createChat({ user_ids: ids, chat_name: newChatObj.chat_name });

                // Merge the new ID from server with the contact info
                activeChat = {
                    ...newChatObj,
                    chat_id: response.chat_id
                };
            } catch (error) {
                console.error("Failed to create chat session", error);
                return; // Stop if we can't get an ID
            }
        }

        // Check if chat already exists in recent chats
        const existingChat = recentChats.find(c => c.chat_id === activeChat.chat_id);

        // Optimistically add to recent chats if not already present
        const optimisticChat = {
            chat_id: activeChat.chat_id,
            chat_name: activeChat.chat_name,
            chat_image: activeChat.chat_image,
            is_group: activeChat.is_group || 0,
            is_online: activeChat.is_online || 0,
            is_mentor: activeChat.is_mentor || 0,
            unread_messages: 0,
            last_message: {
                content: "Tap to start chatting",
                created_at: new Date().toISOString() // Current time
            }
        };

        if (!existingChat) {
            setRecentChats(prev => [optimisticChat, ...prev]);
        }

        // Set selected chat immediately to open chat area
        setSelectedChat(existingChat || optimisticChat);
        console.log('Chat selected:', existingChat || optimisticChat);

        navigate(`/chats/${activeChat.chat_id}`, { replace: true });
        setSidebarView('recent');
    };


    //debounced fetchRecentChats
    const [searchTerm, setSearchTerm] = useState('');
    const debounceRef = useRef(null);
    const searchTermRef = useRef(searchTerm);

    useEffect(() => {
        searchTermRef.current = searchTerm;
    }, [searchTerm]);

    const fetchRecentChats = useCallback((pageNo = 1) => {
        getChats(pageNo, searchTermRef.current)
            .then((data) => {
                console.log("Fetched recent chats:", data);
                const newChats = data.chats || [];
                if (pageNo === 1) {
                    setRecentChats(newChats);
                } else {
                    setRecentChats((prev) => [...prev, ...newChats]);
                }
                setChatHasMore(data.has_more_pages);
                setChatPage(pageNo);
            })
            .catch((err) => console.error("Error loading chats", err));
    }, []);


    // 🔹 Initial load when drawer/modal opens
    useEffect(() => {
        if (open) {
            fetchRecentChats(1);
        }
    }, [open, fetchRecentChats]);

    // 🔹 Debounce search
    useEffect(() => {
        if (!open) return;

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            fetchRecentChats(1);
        }, 500); // 500ms debounce
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, fetchRecentChats]);


    const fetchMessages = async (pageNo = 1) => {
        // Check the Ref (Instant) instead of State (Async)
        if (!selectedChat || loadingRef.current) return;

        loadingRef.current = true; // Lock instantly
        setIsFetching(true); // Update UI

        try {
            const data = await getMessages(selectedChat.chat_id, pageNo);
            console.log("Fetched messages for chat", data);

            const newMessages = data.messages || [];

            if (pageNo === 1) {
                setMessages(newMessages);
                scrollToBottom("auto");
            } else {
                setMessages((prev) => [...prev, ...newMessages]);
            }

            setIsBlocked(data.is_blocked);
            setIsConnected(data.is_connected);
            setShowMsg(data.show_msg)
            setReceipientId(data.recipient_user_id);

            setHasMore(data.has_more_pages);
            setPage(pageNo);
        } catch (err) {
            console.error("Error loading messages", err);
            showSnackbar(err.message || "Failed to load messages", "error");
        } finally {
            loadingRef.current = false; // Unlock
            setIsFetching(false);
        }
    };

    const loadMore = () => {
        // Check Ref here too
        if (loadingRef.current || !hasMore) return;

        if (scrollRef.current) {
            prevHeightRef.current = scrollRef.current.scrollHeight;
        }
        fetchMessages(page + 1);
    };

    useEffect(() => {
        if (selectedChat) {
            setMessages([]);
            setPage(1);
            setHasMore(true);

            // Reset locks
            setIsFetching(false);
            loadingRef.current = false;

            prevHeightRef.current = null;

            fetchMessages(1);
        } else {
            setMessages([]);
        }
    }, [selectedChat?.chat_id]);


    useEffect(() => {
        if (selectedChat) {
            setMessages([]);
            setPage(1);
            setHasMore(true);
            setIsFetching(false);
            prevHeightRef.current = null;

            fetchMessages(1);
        } else {
            setMessages([]);
        }
    }, [selectedChat?.chat_id]);


    useLayoutEffect(() => {
        if (!isFetching && prevHeightRef.current && scrollRef.current) {
            // New Total Height - Old Total Height = The height of the new content added at top
            const newHeight = scrollRef.current.scrollHeight;
            const diff = newHeight - prevHeightRef.current;

            // Instantly adjust scroll so user sees the exact same message they were looking at
            scrollRef.current.scrollTop = diff;

            prevHeightRef.current = null;
        }
    }, [messages, isFetching]);


    const scrollToBottom = (behavior = "smooth") => {
        setTimeout(() => {
            bottomRef.current?.scrollIntoView({ behavior, block: "end" });
        }, 100);
    };

    const handleViewImagePreview = (imageBlob) => {
        const imageUrl = getImageUrlWithToken(imageBlob);
        setPreviewImageUrl(imageUrl);
        setIsPreviewOpen(true);
    };

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const isMenuOpen = Boolean(menuAnchorEl);

    const handleMenuOpen = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    // ACTIONS
    const handleCloseChat = () => {
        setSelectedChat(null);
        setIsDetailOpen(false);
        navigate('/chats', { replace: true });
        handleMenuClose();
    };

    const handleClearChat = () => {
        if (!selectedChat) return;

        clearChat(selectedChat.chat_id)
            .then((res) => {
                console.log("Chat deleted:", res);
                setMessages([]);
                setRecentChats((prev) => prev.map((chat) => {
                    if (chat.last_message && String(chat.chat_id) === String(selectedChat.chat_id)) {
                        return { ...chat, last_message: null };
                    }
                    return chat;
                }));
                handleMenuClose();
                showSnackbar("Chat Cleared successfully", "success");
                setIsDetailOpen(false);
            })
            .catch((error) => {
                console.error("Error clearing chat:", error);
            });
    };

    const handleDeleteChat = () => {
        if (!selectedChat) return;
        deleteChat(selectedChat.chat_id)
            .then((res) => {
                console.log("Chat deleted:", res);
                setMessages([]);
                //filter out deleted chat from recentChats
                setRecentChats((prevChats) =>
                    prevChats.filter((chat) => chat.chat_id !== selectedChat.chat_id)
                );
                setSelectedChat(null);
                navigate('/chats', { replace: true });
                handleMenuClose();
                setIsDetailOpen(false);
            })
            .catch((error) => {
                console.error("Error deleting chat:", error);
            });
    };

    const messageReactions = [
        {
            id: 1,
            emoji: "/assets/icons/like.png",
            label: "Like",
        },
        {
            id: 2,
            emoji: "/assets/icons/love.png",
            label: "Love",
        },
        {
            id: 3,
            emoji: "/assets/icons/what_a_content.png",
            label: "What a content",
        },
        {
            id: 4,
            emoji: "/assets/icons/award_winning.png",
            label: "Award Winning",
        },
        {
            id: 5,
            emoji: "/assets/icons/lets_make_it.png",
            label: "Let's make it",
        },
        {
            id: 6,
            emoji: "/assets/icons/excited.png",
            label: "Excited",
        },
        {
            id: 7,
            emoji: "/assets/icons/must_watch.png",
            label: "Must watch",
        },
        {
            id: 8,
            emoji: "/assets/icons/support.png",
            label: "Support",
        },
    ];

    const [activeEmojiPicker, setActiveEmojiPicker] = useState(null); // anchor element
    const [emojiTargetMsgId, setEmojiTargetMsgId] = useState(null);   // message id

    const handleShowEmojiPicker = (event, msgId) => {
        setActiveEmojiPicker(event.currentTarget);
        setEmojiTargetMsgId(msgId);
    };

    const handleHideEmojiPicker = () => {
        setActiveEmojiPicker(null);
        setEmojiTargetMsgId(null);
    };

    const handleEmojiSelect = (reactionId, msgId) => {
        const data = {
            reaction: reactionId,
        };
        addReaction(msgId, data)
            .then((res) => {
                //console.log(res);
                setMessages((prev) =>
                    prev.map((msg) => {
                        if (msg.id !== msgId) return msg;

                        const reactions = msg.reactions || [];
                        const existingReaction = reactions.find(
                            (reaction) => reaction.user_id === user.id
                        );

                        // If the same reaction exists, remove it; otherwise, update or add
                        const updatedReactions =
                            existingReaction?.reaction === reactionId
                                ? reactions.filter((reaction) => reaction.user_id !== user.id)
                                : [
                                    ...reactions.filter(
                                        (reaction) => reaction.user_id !== user.id
                                    ),
                                    { user_id: user.id, reaction: reactionId },
                                ];

                        return { ...msg, reactions: updatedReactions };
                    })
                );

                setActiveEmojiPicker(null); // Close picker after selection
            })
            .catch((error) => {
                console.error("Failed to add reaction:", error);
            });
    };

    // Realtime message handling
    useEffect(() => {
        if (!data || !selectedChat || !data.messages) return;
        const newMessage = data.messages;

        // Only add if message belongs to currently open chat
        if (String(newMessage.chat_id) !== String(selectedChat.chat_id)) return;

        // console.log("Realtime message received:", newMessage);

        setMessages(prevMessages => {
            // ⛔ Prevent duplication
            const alreadyExists = prevMessages.some(
                msg => msg.id === newMessage.id
            );
            if (alreadyExists) return prevMessages;
            return [newMessage, ...prevMessages];
        });

        // If user is already near bottom, scroll to new message
        scrollToBottom("smooth");

    }, [data, selectedChat]);


    // Real-time chat list update
    useEffect(() => {
        if (!data?.messages) return;
        const newMessage = data.messages;

        // Prevent re-processing same message when selectedChat changes
        if (lastProcessedMsgIdRef.current === newMessage.id) return;
        lastProcessedMsgIdRef.current = newMessage.id;

        console.log("Realtime chat update received:", newMessage);

        setRecentChats((prevChats) => {
            const chatIndex = prevChats.findIndex((c) => String(c.chat_id) === String(newMessage.chat_id));
            const isSystem = newMessage.is_SystemMsg === 1; // Check if system message

            // Determine if it's my message (to avoid incrementing unread for own messages)
            const isMyMessage = String(newMessage.sender_id) === String(user?.id);
            // Check if this chat is currently open
            const isCurrentChat = selectedChat && String(selectedChat.chat_id) === String(newMessage.chat_id);


            // Prepare last message content preview
            let previewContent = newMessage.content;
            if (!previewContent && newMessage.attachments?.length > 0) {
                previewContent = "Sent an attachment";
            }
            if (isSystem && !previewContent) {
                previewContent = "System Message";
            }


            if (chatIndex > -1) {
                const updatedChat = { ...prevChats[chatIndex] };
                updatedChat.last_message = {
                    content: previewContent,
                    created_at: newMessage.created_at
                };

                if (!isMyMessage && !isCurrentChat && !isSystem) {
                    updatedChat.unread_messages = (updatedChat.unread_messages || 0) + 1;
                }

                // Move chat to top
                const newChats = [...prevChats];
                newChats.splice(chatIndex, 1);
                return [updatedChat, ...newChats];

            } else {
                // Chat not in list, add it (like "optimistic" but from real event)

                const newChat = {
                    chat_id: newMessage.chat_id,
                    chat_name: newMessage.chat_name,
                    chat_image: newMessage.chat_image,
                    is_group: newMessage.is_group || 0,
                    is_online: newMessage.is_online || 0,
                    unread_messages: (!isMyMessage && !isSystem) ? 1 : 0,
                    last_message: {
                        content: previewContent,
                        created_at: newMessage.created_at
                    },
                };
                return [newChat, ...prevChats];
            }
        });

    }, [data, user, selectedChat]);



    useEffect(() => {
        if (data?.messages && selectedChat && String(data.messages.chat_id) === String(selectedChat.chat_id)) {
            // Realtime message handles updating the list. This effect purely marks as seen.
            console.log("Marking message as seen:", data.messages);

            const messageId = data.messages.id;
            // Only mark seen if it is NOT system message and NOT sent by me
            const isSystem = data.messages.is_SystemMsg === 1;
            const isMyMessage = String(data.messages.sender_id) === String(user?.id);

            if (!isSystem && !isMyMessage) {
                messageSeen(selectedChat.chat_id, messageId, 'seen')
                    .then((res) => {
                        // console.log("message seen", res);
                    })
                    .catch((error) => {
                        console.error("Failed to seen message:", error);
                    });
            }
        }
    }, [data, selectedChat, user]);

    useEffect(() => {
        if (data?.message_ids?.length && selectedChat) {
            setMessages((prev) =>
                prev.map((msg) => {
                    if (data.message_ids.includes(msg.id)) {
                        return { ...msg, is_read: 1 };
                    }
                    return msg;
                })
            );
        }
    }, [data, selectedChat]);

    const [unreadMessages, setUnreadMessages] = useState(0);

    useEffect(() => {
        if (user) {
            getNewMessageCount()
                .then((response) => {
                    // console.log("unread messages count", response);
                    setUnreadMessages(response.total_new_messages);
                })
                .catch((error) => {
                    console.error(error);
                });
        }
    }, [user]);

    // Real-time message count updates
    useEffect(() => {
        if (data?.unread_chat_notifications_count) {
            // console.log("realtime chat notifi", data.unread_chat_notifications_count);
            setUnreadMessages(data.unread_chat_notifications_count);
        }
    }, [data]);

    // Reset count when on /chats page
    useEffect(() => {
        if (location.pathname.startsWith('/chats')) {
            setUnreadMessages(0);
            markRead().catch((error) => {
                console.error(error);
            });
        }
    }, [location.pathname]);


    return (
        <React.Fragment>
            {bottombar ? (
                <Button fullWidth
                    startIcon={<ChatBubbleRounded />}
                    sx={{
                        flexDirection: "column",
                        fontSize: "0.75rem",
                        textTransform: "none",
                        color: location.pathname.startsWith("/chats")
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                        "& .MuiButton-startIcon": {
                            marginRight: 0,
                        }
                    }}
                    onClick={() => navigate("/chats")}
                >
                    Messages
                </Button>
            ) : (

                <Badge badgeContent={unreadMessages}
                    color="error"
                    overlap="circular"
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <IconButton sx={{ display: { xs: "none", md: "flex" }, backgroundColor: theme.palette.action.hover }}
                        onClick={() => navigate("/chats")}
                    >
                        <ChatBubbleRounded />
                    </IconButton>
                </Badge>
            )}


            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                slotProps={{
                    paper: {
                        sx: { width: '100%' }
                    }
                }}
            >

                <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden', bgcolor: 'background.paper' }}>

                    <CssBaseline />

                    {/* SIDEBAR CONTAINER */}
                    <Box
                        component="nav"
                        sx={{
                            borderRight: '1px solid',
                            borderColor: 'divider',
                            width: { xs: '100%', sm: 400 },
                            height: '100%',
                            display: { xs: selectedChat ? 'none' : 'flex', sm: 'flex' },
                            flexDirection: 'column',
                            flexShrink: 0,
                            zIndex: 2
                        }}
                    >
                        <Branding onClose={onClose} />
                        {/* DYNAMIC CONTENT AREA (Flex Grow) */}
                        <Box sx={{ flexGrow: 1, overflow: 'hidden', position: 'relative' }}>
                            {sidebarView === 'recent' ? (
                                <RecentChatList
                                    chats={recentChats}
                                    hasMore={chatHasMore}
                                    loadMore={() => fetchRecentChats(chatPage + 1)}
                                    selectedId={selectedChat?.chat_id}
                                    onSelect={handleChatSelect}
                                    onSwitchToContacts={() => setSidebarView('contacts')}
                                    onClose={onClose}
                                    getImageUrlWithToken={getImageUrlWithToken}
                                    user={user}
                                    searchTerm={searchTerm}
                                    setSearchTerm={setSearchTerm}
                                />
                            ) : (
                                <ConnectionList
                                    user={user}
                                    onBack={() => setSidebarView('recent')}
                                    onStartChat={handleStartNewChat}
                                    getImageUrlWithToken={getImageUrlWithToken}
                                />
                            )}
                        </Box>

                    </Box>



                    {/* CHAT AREA */}
                    <Box
                        sx={{
                            flexGrow: 1,
                            display: { xs: selectedChat ? 'flex' : 'none', sm: 'flex' },
                            flexDirection: 'column',
                            height: '100%',
                            position: 'relative',
                            minWidth: 0 // Prevents flex overflow issues
                        }}
                    >
                        {!selectedChat ? (
                            <EmptyState />
                        ) : (
                            <>
                                {/* 1. Header */}
                                <AppBar position="static" elevation={0} color="default"
                                    sx={{
                                        zIndex: 1,
                                        backgroundColor: theme.palette.background.paper,
                                        boxShadow: theme.palette.mode === "light" ? "0px 1px 3px rgba(0, 0, 0, 0.1)" : "0px 1px 3px rgba(0, 0, 0, 0.5)", flexShrink: 0
                                    }}
                                >
                                    <Toolbar>
                                        <IconButton edge="start" color="inherit" onClick={() => {
                                            setSelectedChat(null);
                                            navigate('/chats', { replace: true });
                                        }} sx={{ mr: 1, display: { sm: 'none' } }}>
                                            <ArrowBack />
                                        </IconButton>
                                        <Box
                                            onClick={() => setIsDetailOpen(true)} // 5. Trigger Open
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                flexGrow: 1,
                                                cursor: 'pointer', // Show hand cursor
                                            }}
                                        >

                                            {selectedChat.is_mentor ? (
                                                <MentorBadge isSmall>
                                                    <Avatar src={getImageUrlWithToken(selectedChat.chat_image)} />
                                                </MentorBadge>
                                            ) : (
                                                <Avatar alt={selectedChat.chat_name} src={getImageUrlWithToken(selectedChat.chat_image)}>
                                                    {selectedChat.is_group === 1 && <Groups />}
                                                </Avatar>
                                            )}


                                            <Box sx={{ flexGrow: 1, ml: 2 }}>
                                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                                                    <Typography variant="body1" fontWeight="bold"
                                                        sx={{
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 1,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        {selectedChat.chat_name}
                                                    </Typography>
                                                    {selectedChat.is_verified && <VerifiedUser fontSize="small" color="success" sx={{ fontSize: 16, mb: 0.3 }} />}
                                                </Stack>
                                                <Typography variant="caption"
                                                    sx={{
                                                        color: selectedChat.is_group === 1 ? theme.palette.primary.main : selectedChat.is_online === 1 ? theme.palette.success.main : theme.palette.text.secondary,
                                                    }}
                                                >
                                                    {selectedChat.is_group === 1 ? 'Group' : selectedChat.is_online === 1 ? 'Online' : 'Offline'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <IconButton onClick={handleMenuOpen}>
                                            <MoreVert />
                                        </IconButton>

                                    </Toolbar>

                                    <Menu
                                        anchorEl={menuAnchorEl}
                                        open={isMenuOpen}
                                        onClose={handleMenuClose}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                    >
                                        <MenuItem onClick={() => { setIsDetailOpen(true); handleMenuClose(); }}>
                                            <ListItemIcon>
                                                <InfoOutline fontSize="small" />
                                            </ListItemIcon>
                                            See Info
                                        </MenuItem>

                                        <MenuItem onClick={handleCloseChat}>
                                            <ListItemIcon>
                                                <Close fontSize="small" />
                                            </ListItemIcon>
                                            Close Chat
                                        </MenuItem>

                                        <MenuItem onClick={handleClearChat}>
                                            <ListItemIcon>
                                                <RemoveCircleOutline fontSize="small" />
                                            </ListItemIcon>
                                            Clear Chat
                                        </MenuItem>
                                        <MenuItem onClick={handleDeleteChat}>
                                            <ListItemIcon>
                                                <DeleteForeverOutlined fontSize="small" />
                                            </ListItemIcon>
                                            Delete Chat
                                        </MenuItem>
                                    </Menu>

                                </AppBar>

                                {/* 2. Messages Area (MANUAL IMPLEMENTATION) */}
                                <Box
                                    ref={scrollRef} // Bind ref here
                                    // Handle Scroll hitting top (for when scrollbar exists)
                                    onScroll={(e) => {
                                        if (e.target.scrollTop === 0) loadMore();
                                    }}
                                    // Handle Wheel Scroll Up (for when NO scrollbar exists)
                                    onWheel={(e) => {
                                        if (e.deltaY < 0 && e.currentTarget.scrollTop === 0) loadMore();
                                    }}
                                    sx={{
                                        flexGrow: 1,
                                        display: 'flex',
                                        flexDirection: 'column', // Standard Column (Top to Bottom)
                                        p: 3,
                                        overflowY: 'auto',
                                        backgroundColor: theme.palette.background.paper,
                                    }}
                                >
                                    {/* LOADER AT TOP */}
                                    {(hasMore || isFetching) && (
                                        <Box sx={{ textAlign: "center", py: 2, flexShrink: 0, width: '100%' }}>
                                            <CircularProgress size={20} />
                                        </Box>
                                    )}

                                    {/* REVERSE MAP: Render Oldest -> Newest */}
                                    {/* Create a copy of array before reversing to avoid mutating state */}
                                    {[...messages].reverse().map((msg) => {
                                        const isMe = msg.sender_id === user?.id;
                                        const isSystem = msg.is_SystemMsg === 1;

                                        if (isSystem) {
                                            return (
                                                <Box key={msg.id} sx={{ display: "flex", justifyContent: "center", my: 1 }}>
                                                    <Typography variant="caption"
                                                        sx={{
                                                            px: 2, py: 0.5,
                                                            borderRadius: 10,
                                                            fontStyle: "italic",
                                                            backgroundColor: theme.palette.action.hover,
                                                            color: theme.palette.primary.main,
                                                            textTransform: "capitalize"
                                                        }}>
                                                        {msg.content}
                                                    </Typography>
                                                </Box>
                                            );
                                        }

                                        return (
                                            <Box key={msg.id}
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: isMe ? "flex-end" : "flex-start",
                                                    mb: 1,
                                                }}
                                            >
                                                <Box sx={{ maxWidth: 320 }}>
                                                    <Paper
                                                        elevation={0}
                                                        sx={{
                                                            p: 1.5,
                                                            bgcolor: isMe ? "background.default" : theme.palette.action.hover,
                                                            borderRadius: isMe ? "20px 20px 4px 20px" : "20px 20px 20px 4px",
                                                            position: "relative"
                                                        }}
                                                    >
                                                        <Box sx={{ display: "flex", alignItems: "center", mb: 0.5, justifyContent: isMe ? "space-between" : "flex-start" }}>
                                                            <Typography variant="body2" color="textSecondary">
                                                                {formatDisplayName(msg.sender?.name, {
                                                                    maxLength: 36,
                                                                    truncateAtSpace: true, // Enable truncation at space
                                                                })}

                                                            </Typography>
                                                            {isMe && (
                                                                <DeleteMessage deletemsgId={msg.id} setMessages={setMessages} setRecentChats={setRecentChats} />
                                                            )}
                                                        </Box>


                                                        {/* 🔹 IMAGE MESSAGE */}
                                                        {msg.attachments && msg.attachments.length > 0 ? (
                                                            <Box>
                                                                <Box sx={{
                                                                    height: 200,
                                                                    mb: msg.content ? 1 : 0,
                                                                    overflow: "hidden"
                                                                }}>
                                                                    <img
                                                                        src={getImageUrlWithToken(msg.attachments[0].file_blob)}
                                                                        alt="attachment"
                                                                        style={{
                                                                            width: "100%",
                                                                            height: "100%",
                                                                            objectFit: "cover",
                                                                            borderRadius: 10,
                                                                            cursor: "pointer"
                                                                        }}
                                                                        onClick={() => handleViewImagePreview(msg.attachments[0].file_blob)}
                                                                    />
                                                                </Box>

                                                                {msg.content && (
                                                                    <ReadMoreText chatarea={true} text={msg.content || ""} maxLength={150} />
                                                                )}
                                                            </Box>
                                                        ) : (
                                                            <ReadMoreText chatarea={true} text={msg.content || ""} maxLength={150} />
                                                        )}


                                                        <Box sx={{ display: "flex", alignItems: "center", mt: 0.5, justifyContent: "space-between" }}>
                                                            {!isMe && (
                                                                <Typography variant="caption"
                                                                    sx={{ display: "block", textAlign: isMe ? "right" : "left", color: "text.secondary", fontSize: "0.7rem" }}>
                                                                    {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                                                </Typography>
                                                            )}

                                                            <Tooltip title="React" arrow placement="top">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => handleShowEmojiPicker(e, msg.id)}
                                                                >
                                                                    {emojiTargetMsgId === msg.id ? (
                                                                        <EmojiEmotions fontSize="20px" />
                                                                    ) : (
                                                                        <EmojiEmotionsOutlined fontSize="20px" />
                                                                    )}
                                                                </IconButton>
                                                            </Tooltip>

                                                            {isMe && (
                                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                                    <Typography variant="caption"
                                                                        sx={{ display: "block", textAlign: isMe ? "right" : "left", color: "text.secondary", fontSize: "0.7rem" }}>
                                                                        {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                                                    </Typography>


                                                                    <Box
                                                                        sx={{ ml: 2, display: "flex", alignItems: "center" }}
                                                                    >
                                                                        {msg.is_read ? (
                                                                            <DoneAll fontSize="16px" color="success" />
                                                                        ) : (
                                                                            <Check fontSize="16px" color="disabled" />
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            )}
                                                        </Box>


                                                        {msg.reactions && msg.reactions.length > 0 && (
                                                            <Paper
                                                                sx={{
                                                                    display: "flex",
                                                                    gap: 1,
                                                                    position: "absolute",
                                                                    bottom: 0,
                                                                    left: isMe ? "auto" : 0,
                                                                    right: isMe ? 0 : "auto",
                                                                    zIndex: 1,
                                                                    transform: "translateY(50%)",
                                                                    transition: "transform 0.2s",
                                                                    boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
                                                                    borderRadius: 10,
                                                                    p: 0.5,
                                                                }}
                                                            >
                                                                {messageReactions.map((reaction) => {
                                                                    const reactionCount = msg.reactions.filter(
                                                                        (r) => r.reaction === reaction.id
                                                                    ).length;

                                                                    return (
                                                                        reactionCount > 0 && (
                                                                            <Box
                                                                                key={reaction.id}
                                                                                sx={{ display: "flex", alignItems: "center" }}
                                                                            >
                                                                                <img
                                                                                    src={reaction.emoji}
                                                                                    alt={reaction.label}
                                                                                    style={{ width: 16, height: 16 }}
                                                                                />
                                                                                <Typography variant="caption" sx={{ ml: 0.5 }}>
                                                                                    {reactionCount}
                                                                                </Typography>
                                                                            </Box>
                                                                        )
                                                                    );
                                                                })}
                                                            </Paper>
                                                        )}

                                                        {emojiTargetMsgId === msg.id && !msg.is_SystemMsg && (
                                                            <Menu
                                                                anchorEl={activeEmojiPicker}
                                                                open={Boolean(activeEmojiPicker)}
                                                                onClose={handleHideEmojiPicker}
                                                                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                                                                transformOrigin={{ horizontal: "right", vertical: "top" }}
                                                                slotProps={{
                                                                    paper: {
                                                                        elevation: 0,
                                                                        sx: {
                                                                            overflow: "visible",
                                                                        },
                                                                    },
                                                                }}
                                                            >
                                                                <Paper
                                                                    sx={{
                                                                        display: "flex",
                                                                        gap: 1,
                                                                        alignItems: "center",
                                                                        borderRadius: { xs: 2, sm: 10 },
                                                                        px: 1,
                                                                        py: { xs: 1, sm: 0.5 },
                                                                        boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
                                                                        flexWrap: "wrap",
                                                                    }}

                                                                >
                                                                    {messageReactions.map((emoji) =>
                                                                        isMobile ? (
                                                                            <Box
                                                                                key={emoji.id}
                                                                                sx={{
                                                                                    display: "flex",
                                                                                    flexDirection: "column",
                                                                                    alignItems: "center",
                                                                                    cursor: "pointer",
                                                                                    "&:hover": {
                                                                                        transform: "scale(1.1)",
                                                                                        transition: "transform 0.2s",
                                                                                    },
                                                                                }}
                                                                                onClick={() => handleEmojiSelect(emoji.id, msg.id)}
                                                                            >
                                                                                <Box
                                                                                    sx={{
                                                                                        display: "flex",
                                                                                        width: 18,
                                                                                        height: 18,
                                                                                    }}
                                                                                    component={"img"}
                                                                                    src={emoji.emoji}
                                                                                />
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    color="textSecondary"
                                                                                    align="center"
                                                                                    sx={{ fontSize: "0.65rem" }}
                                                                                >
                                                                                    {emoji.label}
                                                                                </Typography>
                                                                            </Box>
                                                                        ) : (
                                                                            <Tooltip
                                                                                key={emoji.id}
                                                                                title={emoji.label}
                                                                                placement="top"
                                                                                arrow
                                                                            >
                                                                                <Box
                                                                                    sx={{
                                                                                        display: "flex",
                                                                                        width: 24,
                                                                                        height: 24,
                                                                                        ml: 0.5,
                                                                                        cursor: "pointer",
                                                                                        "&:hover": {
                                                                                            transform: "scale(1.2)",
                                                                                            transition: "transform 0.2s",
                                                                                        },
                                                                                    }}
                                                                                    onClick={() => handleEmojiSelect(emoji.id, msg.id)}
                                                                                    component={"img"}
                                                                                    src={emoji.emoji}
                                                                                />
                                                                            </Tooltip>
                                                                        )
                                                                    )}
                                                                </Paper>
                                                            </Menu>
                                                        )}
                                                    </Paper>



                                                </Box>
                                            </Box>
                                        );
                                    })}

                                    {/* Anchor div to help scrollToBottom */}
                                    <div ref={bottomRef} style={{ height: 1 }} />

                                </Box>

                                {/* 3. Input Area */}
                                <Box
                                    sx={{
                                        p: 1,
                                        bgcolor: 'background.paper',
                                        borderTop: '1px solid',
                                        borderColor: 'divider',
                                        flexShrink: 0 // Prevents input from disappearing
                                    }}
                                >
                                    {/* Pass a focus handler to your MessageInput */}
                                    <div onFocus={() => {
                                        // Wait 300ms for the keyboard to fully animate up, then scroll
                                        setTimeout(() => {
                                            bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
                                        }, 300);
                                    }}>
                                        <MessageInput chatId={selectedChat.chat_id} showSnackbar={showSnackbar}
                                            setMessages={setMessages}
                                            receipientId={receipientId} isBlocked={isBlocked} isConnected={isConnected} showMsg={showMsg}
                                            setShowMsg={setShowMsg} setIsConnected={setIsConnected} setIsBlocked={setIsBlocked}
                                        />
                                    </div>
                                </Box>
                            </>
                        )}
                    </Box>


                    {/* CHAT DETAIL CONTAINER */}
                    <Slide direction="left" in={isDetailOpen} mountOnEnter unmountOnExit>
                        <Box sx={{
                            width: { xs: '100%', sm: 400 },
                            height: '100%',
                            borderLeft: '1px solid',
                            borderColor: 'divider',
                            position: { xs: 'absolute', md: 'static' }, // Absolute on mobile to cover, static on desktop to sit side-by-side
                            top: 0,
                            right: 0,
                            zIndex: 10
                        }}>
                            <ChatDetail chat={selectedChat} onClose={() => setIsDetailOpen(false)}
                                closeChat={onClose}
                                setSelectedChat={setSelectedChat}
                                setRecentChats={setRecentChats}
                                showSnackbar={showSnackbar}
                                username={user?.username} navigate={navigate}
                                currentUserId={user?.id} getImageUrlWithToken={getImageUrlWithToken} />
                        </Box>
                    </Slide>
                </Box>

                <ImagePreviewDialog
                    open={isPreviewOpen}
                    onClose={() => setIsPreviewOpen(false)}
                    imageUrl={previewImageUrl}
                />

            </Drawer>
        </React.Fragment>
    );
}

export default Messages;